import type { AccountViewResponse } from '@/types/core-api';
import type { DebtData } from '@/types';
import { fromMillicents } from '@/lib/currency';

/**
 * The account's credit and what it can be charged, in currency units.
 *
 * A credit belongs to the account, not to any one debt. The server attributes it across
 * the open debts oldest-first so each one can be shown a figure that adds up to this
 * total — but nothing is posted and no balance moves, so which debt it actually clears
 * stays the ledger's decision.
 */
export interface AccountNet {
  /** What the open debts add up to, before credit. */
  owed: number;
  /** Credit held, as a positive amount. Zero for the overwhelming majority of accounts. */
  credit: number;
  /** `owed − credit`, floored at zero — the account-level charge. */
  netPayable: number;
  currency: string | null;
}

/**
 * Read the account net off the account view, or null when the server declined to compute
 * one — an account whose open cases span currencies has no single total, and the portal
 * renders a figure per currency there rather than one hero amount.
 */
export function accountNetFromView(view: AccountViewResponse): AccountNet | null {
  const { account_owed_millicents: owed, account_credit_millicents: credit } = view;
  const net = view.account_net_payable_millicents;
  if (owed == null || credit == null || net == null) return null;
  return {
    owed: fromMillicents(owed),
    credit: fromMillicents(credit),
    netPayable: fromMillicents(net),
    currency: view.account_net_currency,
  };
}

/**
 * The credit attributed to this set of debts.
 *
 * Summed per surface rather than taken from the account total, because a surface can hold
 * fewer debts than the account does — the account-payment page drops debts under an
 * arrangement. Taking the whole account's credit off a narrower subtotal would credit that
 * surface for money attributed to a debt it cannot collect against.
 */
export function creditApplied(debts: Pick<DebtData, 'creditApplied'>[]): number {
  return debts.reduce((total, debt) => total + (debt.creditApplied ?? 0), 0);
}

/** A subtotal less the credit attributed to it, floored at zero. */
export function applyCredit(subtotal: number, credit: number): number {
  return Math.max(0, subtotal - credit);
}

/**
 * Whether the credit is what leaves nothing to pay.
 *
 * Narrower than `applyCredit(...) <= 0` on purpose: an account that simply owes nothing
 * has always reached these surfaces the way it does today, and this change has no business
 * altering that. Only a balance the credit cleared is new behaviour.
 */
export function creditClearsBalance(subtotal: number, credit: number): boolean {
  return credit > 0 && applyCredit(subtotal, credit) <= 0;
}

/** What a single debt is charged: its balance less the credit attributed to it. */
export function debtPayable(debt: Pick<DebtData, 'currentBalance' | 'creditApplied'>): number {
  return applyCredit(debt.currentBalance, debt.creditApplied ?? 0);
}
